import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, Map, FileSpreadsheet, Truck, Lock, Users, 
  Zap, Clock, Crown, Paintbrush, Brain, Coins 
} from 'lucide-react';
import { Button } from '../common/Button';

export const SubscriptionPlans = () => {
  const navigate = useNavigate();

  const mainPlans = [
    {
      id: 'viewer',
      name: 'Viewer Only',
      price: 'Free',
      subtitle: 'Limited Access',
      features: [
        'View shared maps',
        'Access to shared surveys',
        'Community support'
      ],
      signupPath: '/signup/free',
      buttonText: 'Start Free',
      color: 'bg-gray-800/50'
    },
    {
      id: 'beta',
      name: 'Beta Tester',
      price: 'Free',
      subtitle: 'Early Access Program',
      features: [
        'Full platform access',
        'All modules included',
        '14 days access',
        'Early access to features',
        'Feedback required'
      ],
      signupPath: '/signup/beta',
      buttonText: 'Join Beta Program',
      color: 'bg-gray-800/50',
      badge: 'Limited Time'
    },
    {
      id: 'professional',
      name: 'Professional',
      price: 'CAD $49',
      interval: '/month',
      subtitle: '15% off yearly',
      features: [
        'RoadScope survey',
        'Map module',
        'Create surveys',
        'Advanced route planning',
        'Unlimited POIs',
        '72 hrs support',
        'Advanced exports'
      ],
      addons: [
        { name: 'RoadGuard Module', price: 10, icon: Truck },
        { name: 'CargoCare Module', price: 10, icon: Shield },
        { name: 'PermitPro Module', price: 10, icon: FileSpreadsheet },
        { name: 'Team Module', price: 5, icon: Users },
        { name: '48h Priority Support', price: 10, icon: Clock },
        { name: 'Next-in-line Support', price: 25, icon: Crown },
        { name: 'Custom Branding', price: 15, icon: Paintbrush },
        { name: 'AI Usage (250 queries)', price: 15, icon: Brain },
        { name: 'Unlimited AI Usage', price: null, icon: Zap, contactSales: true }
      ],
      signupPath: '/signup/professional',
      buttonText: 'Start Free Trial',
      color: 'bg-gray-800/50',
      badge: 'Most Popular'
    }
  ];

  const tokenPacks = [
    {
      id: 'starter',
      name: 'Starter Pack',
      price: 49,
      tokens: 100,
      description: 'Perfect for occasional users needing route planning and map export credits.'
    },
    {
      id: 'pro',
      name: 'Pro Pack',
      price: 199,
      tokens: 500,
      description: 'Ideal for regular users requiring advanced feature access.'
    },
    {
      id: 'enterprise',
      name: 'Enterprise Pack',
      price: 349,
      tokens: 1000,
      description: 'Best value for teams with extended validity and custom solutions.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#3D3F42] flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-7xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Choose Your Plan</h1>
          <p className="text-gray-400">Start with a 14-day free trial</p>
        </div>

        {/* Main Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {mainPlans.map((plan) => (
            <div 
              key={plan.id}
              className={`bg-[#2A2B2E] rounded-lg overflow-hidden relative ${
                plan.badge ? 'ring-3 ring-offset-2 ring-purple-500' : ''
              }`}
            >
              {plan.badge && (
                <div className={`absolute top-1 right-1 px-2 py-1 rounded-full text-xs font-small
                  ${plan.badge === 'Most Popular' ? 'bg-red-500 text-white' : 'bg-purple-500 text-white'}`
                }>
                  {plan.badge}
                </div>
              )}

              {/* Plan Header */}
              <div className={`${plan.color} p-6 text-white`}>
                <h3 className="text-xl font-bold">{plan.name}</h3>
                <div className="mt-4 flex items-baseline">
                  <span className="text-3xl font-bold">{plan.price}</span>
                  {plan.interval && (
                    <span className="ml-1 text-sm opacity-80">{plan.interval}</span>
                  )}
                </div>
                <p className="mt-2 text-sm opacity-80">{plan.subtitle}</p>
              </div>

              {/* Action Button - Moved up */}
              <div className="px-6 pt-6">
                <Button
                  onClick={() => navigate(plan.signupPath)}
                  className={`w-full ${plan.color} hover:opacity-90 text-white`}
                >
                  {plan.buttonText}
                </Button>
              </div>

              {/* Features List */}
              <div className="p-6">
                <ul className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-gray-300">
                      <Shield className="w-4 h-4 mr-2 text-gray-400" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Add-ons for Professional Plan */}
                {plan.addons && (
                  <div className="mt-6 pt-6 border-t border-gray-700">
                    <h4 className="text-white font-medium mb-4">Available Add-ons</h4>
                    <ul className="space-y-3">
                      {plan.addons.map((addon, index) => (
                        <li key={index} className="flex items-center justify-between text-gray-300">
                          <div className="flex items-center">
                            <addon.icon className="w-4 h-4 mr-2 text-gray-400" />
                            {addon.name}
                          </div>
                          <span className="text-gray-400">
                            {addon.contactSales ? 'Contact Us' : `+$${addon.price}/mo`}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Pay as You Go Section */}
        <div className="bg-[#2A2B2E] rounded-lg p-8 mb-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">Pay as You Go</h2>
            <p className="text-gray-400">Flexible Access with Token System</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {tokenPacks.map((pack) => (
              <div key={pack.id} className="bg-gray-800/50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-2">{pack.name}</h3>
                <div className="flex items-baseline mb-4">
                  <span className="text-3xl font-bold text-white">${pack.price}</span>
                  <span className="ml-2 text-gray-400">{pack.tokens} tokens</span>
                </div>
                <p className="text-gray-400 text-sm">{pack.description}</p>
                <Button
                  onClick={() => navigate('/signup/tokens')}
                  className="w-full mt-4 bg-orange-600 hover:bg-orange-700 text-white"
                >
                  Purchase Tokens
                </Button>
              </div>
            ))}
          </div>

          <div className="bg-gray-800/50 rounded-lg p-6">
            <h3 className="text-xl font-bold text-white mb-4">Earn Free Tokens</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                'Share route surveys',
                'Validate data accuracy',
                'Participate in ML training',
                'Provide feature feedback'
              ].map((item, index) => (
                <div key={index} className="flex items-center text-gray-300">
                  <Coins className="w-4 h-4 mr-2 text-orange-400" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Login Link */}
        <div className="text-center">
          <p className="text-gray-400">
            Already have an account?{' '}
            <button 
              onClick={() => navigate('/login')}
              className="text-red-400 hover:text-red-300"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};